import { useCallback, useMemo, useRef, useState } from "react";
import { BuildingsIcon, CheckIcon, PlusIcon, WarningIcon, XIcon } from "@phosphor-icons/react";
import {
  loadDestinations,
  loadSiteLeadCounts,
  sites,
  updateDestination,
} from "../lib/data.ts";
import { useAsync } from "../lib/useAsync.ts";
import { Badge, Button, EmptyState, ErrorState, Panel } from "../components/ui.tsx";
import { DateField } from "../components/DateField.tsx";
import type { Destination } from "@pipeline/types.ts";

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="toggle" style={disabled ? { opacity: 0.55, cursor: "not-allowed" } : undefined}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle__track">
        <span className="toggle__thumb" />
      </span>
      <span style={{ fontSize: "var(--t-sm)" }}>{label}</span>
    </label>
  );
}

/** `DateField` keeps the native input's contract: `YYYY-MM-DD` and nothing else. */
function toDateInput(value: Date | null | undefined): string {
  return value ? value.toISOString().slice(0, 10) : "";
}

/**
 * A date-only string means midnight UTC on that day. Start bounds are
 * inclusive, so 2026-08-17 means "from the very start of the 17th". End bounds
 * are exclusive in the routing check, so the user's 2026-09-17 is stored as the
 * start of the 18th — otherwise picking a last day would silently exclude that
 * whole day, which is not what anyone means by "until the 17th".
 */
function fromDateInput(value: string, kind: "from" | "until"): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  if (kind === "until") parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed;
}

function windowState(
  destination: Destination,
  now: Date,
): { tone: "ok" | "warn" | "neutral"; label: string; detail: string } {
  const from = destination.deliverFrom ?? null;
  const until = destination.deliverUntil ?? null;

  if (!from && !until) {
    return {
      tone: "warn",
      label: "always on",
      detail:
        "No dates set, so every lead this partner's sites produce is eligible — including anything backfilled from before the agreement.",
    };
  }
  if (from && now < from) {
    return {
      tone: "neutral",
      label: "not started",
      detail: `Nothing forwards to this partner until ${from.toISOString().slice(0, 10)}.`,
    };
  }
  if (until && now >= until) {
    return {
      tone: "neutral",
      label: "ended",
      detail: "The partnership window has closed. Leads are no longer forwarded here.",
    };
  }
  if (until) {
    const daysLeft = Math.ceil((until.getTime() - now.getTime()) / 86_400_000);
    return {
      tone: daysLeft <= 7 ? "warn" : "ok",
      label: "active",
      detail:
        daysLeft <= 7
          ? `Ends in ${daysLeft} ${daysLeft === 1 ? "day" : "days"}. Extend the end date to continue.`
          : `Running. ${daysLeft} days remain.`,
    };
  }
  return { tone: "ok", label: "active", detail: "Running, with no end date set." };
}

// ---------------------------------------------------------------------------

/** Good enough for a guardrail; Resend rejects anything craftier at send time. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * One editable address list: chips with removal, plus an inline add input.
 * Enter commits (and keeps the input open on a bad address); blur commits what
 * is valid; Escape cancels. Comma-separated pastes add several at once.
 */
function RecipientRow({
  label,
  emails,
  fallback,
  fallbackTone,
  minOne,
  saving,
  notify,
  onSave,
}: {
  label: string;
  emails: string[];
  /** Ghost text shown when the list is empty. */
  fallback?: string;
  fallbackTone?: "bad";
  /** Block removing the last address — a partner with zero lead recipients is an outage. */
  minOne?: boolean;
  saving: boolean;
  notify: (tone: "ok" | "bad", text: string) => void;
  onSave: (next: string[], message: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState(false);
  // Escape must make blur a cancel, not a commit; state alone races the events.
  const cancelled = useRef(false);

  const stop = () => {
    setAdding(false);
    setDraft("");
    setInvalid(false);
  };

  const commit = (keepOpenOnError: boolean) => {
    const parts = draft
      .split(/[\s,;]+/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      stop();
      return;
    }
    const bad = parts.find((part) => !EMAIL_RE.test(part));
    if (bad) {
      notify("bad", `“${bad}” is not a valid email address.`);
      if (keepOpenOnError) setInvalid(true);
      else stop();
      return;
    }
    const next = [...emails];
    for (const part of parts) {
      if (!next.some((email) => email.toLowerCase() === part.toLowerCase())) next.push(part);
    }
    if (next.length === emails.length) {
      // Everything typed was already on the list.
      stop();
      return;
    }
    onSave(next, parts.length === 1 ? `${parts[0]} added.` : `${parts.length} addresses added.`);
    stop();
  };

  const remove = (email: string) => {
    if (minOne && emails.length === 1) {
      notify("bad", "A partner needs at least one lead recipient — add the new address first.");
      return;
    }
    onSave(
      emails.filter((entry) => entry !== email),
      `${email} removed.`,
    );
  };

  return (
    <div className="recip">
      <span className="recip__label">{label}</span>
      <div className="recip__list">
        {emails.map((email) => (
          <span className="recip__chip" key={email}>
            {email}
            <button
              type="button"
              className="recip__x"
              aria-label={`Remove ${email}`}
              disabled={saving}
              onClick={() => remove(email)}
            >
              <XIcon size={11} weight="bold" />
            </button>
          </span>
        ))}
        {emails.length === 0 && fallback && (
          <span className="recip__fallback" data-tone={fallbackTone}>
            {fallback}
          </span>
        )}
        {adding ? (
          <input
            className="recip__input"
            type="text"
            inputMode="email"
            placeholder="name@company.se"
            aria-label={`Add address to ${label}`}
            aria-invalid={invalid || undefined}
            autoFocus
            value={draft}
            disabled={saving}
            onChange={(event) => {
              setDraft(event.target.value);
              setInvalid(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commit(true);
              }
              if (event.key === "Escape") {
                cancelled.current = true;
                stop();
              }
            }}
            onBlur={() => {
              if (cancelled.current) cancelled.current = false;
              else commit(false);
            }}
          />
        ) : (
          <button
            type="button"
            className="recip__add"
            disabled={saving}
            onClick={() => setAdding(true)}
          >
            <PlusIcon size={11} weight="bold" aria-hidden="true" />
            Add
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DestinationCard({
  destination,
  onSaved,
  notify,
  counts,
}: {
  destination: Destination;
  onSaved: (next: Destination) => void;
  notify: (tone: "ok" | "bad", text: string) => void;
  /** null while still loading; the list renders without figures until then. */
  counts: Record<string, number> | null;
}) {
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (patch: Parameters<typeof updateDestination>[1], message: string) => {
      setSaving(true);
      try {
        const next = await updateDestination(destination.id, patch);
        onSaved(next);
        notify("ok", message);
      } catch {
        notify("bad", "Could not save that change.");
      } finally {
        setSaving(false);
      }
    },
    [destination.id, onSaved, notify],
  );

  const allSites = destination.siteKeys.length === 0;

  const toggleSite = (key: string) => {
    // An empty list means "every site", so the first explicit pick has to start
    // from the full set rather than from nothing.
    const current = allSites ? sites.map((site) => site.key) : destination.siteKeys;
    const next = current.includes(key)
      ? current.filter((entry) => entry !== key)
      : [...current, key];

    void save(
      { siteKeys: next.length === sites.length ? [] : next },
      next.length === sites.length ? "Now receiving every site." : "Site routing updated.",
    );
  };

  // Ordered by volume so the sites that actually matter are at the top, and
  // the long tail of one-lead sites does not lead the list.
  const sitesByVolume = useMemo(() => {
    const withCounts = sites.map((site) => ({ ...site, count: counts?.[site.key] ?? 0 }));
    return withCounts.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [counts]);
  const peakVolume = sitesByVolume[0]?.count ?? 0;

  const looksLikeTest = destination.emails.some((email) => email.endsWith("@gmail.com"));
  const state = windowState(destination, new Date());

  return (
    <section className="panel">
      <header className="panel__head">
        <div className="row row--3">
          <h2 className="panel__title">{destination.name}</h2>
          <Badge tone={destination.active ? "ok" : "neutral"}>
            {destination.active ? "active" : "paused"}
          </Badge>
        </div>
        <Toggle
          checked={destination.active}
          label={destination.active ? "Receiving" : "Paused"}
          disabled={saving}
          onChange={(next) =>
            void save(
              { active: next },
              next ? `${destination.name} is receiving leads.` : `${destination.name} paused.`,
            )
          }
        />
      </header>

      <div className="panel__body destcard">
        <section className="sect">
          <header className="sect__head">
            <div>
              <h3 className="sect__title">Recipients</h3>
              <p className="sect__sub">
                Each lead is emailed to these the moment it arrives. The daily summary keeps its
                own list.
              </p>
            </div>
          </header>

          <div className="stack" style={{ gap: "var(--sp-2)" }}>
            <RecipientRow
              label="Each lead"
              emails={destination.emails}
              fallback="No recipients — leads cannot be forwarded."
              fallbackTone="bad"
              minOne
              saving={saving}
              notify={notify}
              onSave={(next, message) => void save({ emails: next }, message)}
            />
            <RecipientRow
              label="Cc"
              emails={destination.ccEmails}
              saving={saving}
              notify={notify}
              onSave={(next, message) => void save({ ccEmails: next }, message)}
            />
            <RecipientRow
              label="Daily summary"
              emails={destination.dailyReportEmails}
              fallback="Uses the lead recipients."
              saving={saving}
              notify={notify}
              onSave={(next, message) => void save({ dailyReportEmails: next }, message)}
            />
          </div>

          {looksLikeTest && (
            <p
              className="row row--2"
              style={{ fontSize: "var(--t-xs)", color: "var(--warn)", marginTop: "var(--sp-3)" }}
            >
              <WarningIcon size={13} weight="fill" aria-hidden="true" />
              Personal addresses are still set as the recipients. Swap these for the partner&rsquo;s
              real address before going live.
            </p>
          )}
        </section>

        {/* The dates that decide whether a lead is this partner's at all.
            Matched against when the customer submitted, never when we
            ingested — so backfilling history can never retroactively hand a
            partner leads that predate the agreement. */}
        <section className="sect">
          <header className="sect__head">
            <div>
              <h3 className="sect__title">Partnership window</h3>
              <p className="sect__sub">{state.detail}</p>
            </div>
            <Badge tone={state.tone}>{state.label}</Badge>
          </header>

          <div className="drange">
            <DateField
              label="Starts"
              placeholder="No start date"
              value={toDateInput(destination.deliverFrom)}
              disabled={saving}
              onPick={(next) =>
                void save(
                  { deliverFrom: fromDateInput(next, "from") },
                  next ? `Leads from ${next} onwards are eligible.` : "Start date cleared.",
                )
              }
            />
            <span className="drange__sep" aria-hidden="true">
              →
            </span>
            <DateField
              label="Ends"
              placeholder="Open-ended"
              value={toDateInput(
                destination.deliverUntil
                  ? new Date(destination.deliverUntil.getTime() - 86_400_000)
                  : null,
              )}
              disabled={saving}
              onPick={(next) =>
                void save(
                  { deliverUntil: fromDateInput(next, "until") },
                  next
                    ? `Runs through ${next}.`
                    : "End date cleared — this partnership now runs open-ended.",
                )
              }
            />
          </div>
        </section>

        <section className="sect">
          <header className="sect__head">
            <div>
              <h3 className="sect__title">Sites routed here</h3>
              <p className="sect__sub">
                {allSites
                  ? `Every site (${sites.length})`
                  : `${destination.siteKeys.length} of ${sites.length} selected`}
              </p>
            </div>
            <Button
              variant="quiet"
              size="sm"
              disabled={saving || allSites}
              onClick={() => void save({ siteKeys: [] }, "Now receiving every site.")}
            >
              All
            </Button>
          </header>

          {/* A checklist carrying each site's volume, not fifteen identical
              pills. Which sites feed a partner is a real decision, and it is
              made on volume — a wall of equal-weight chips hides exactly the
              information you need to make it. */}
          <ul className="sitelist">
            {sitesByVolume.map((site) => {
              const on = allSites || destination.siteKeys.includes(site.key);
              const share = peakVolume === 0 ? 0 : Math.round((site.count / peakVolume) * 100);
              return (
                <li key={site.key}>
                  <button
                    className="sitelist__row"
                    aria-pressed={on}
                    disabled={saving}
                    onClick={() => toggleSite(site.key)}
                  >
                    <span className="sitelist__check" aria-hidden="true">
                      {on && <CheckIcon size={11} weight="bold" />}
                    </span>
                    <span className="sitelist__name">{site.name}</span>
                    <span className="sitelist__track" aria-hidden="true">
                      <span className="sitelist__fill" style={{ inlineSize: `${share}%` }} />
                    </span>
                    <span className="sitelist__count" data-numeric>
                      {counts === null ? "" : site.count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="sect">
          <header className="sect__head">
            <div>
              <h3 className="sect__title">Options</h3>
            </div>
          </header>
          <div className="row row--wrap" style={{ gap: "var(--sp-5)" }}>
            <Toggle
              checked={destination.minQuality === "suspect"}
              label="Also send flagged leads"
              disabled={saving}
              onChange={(next) =>
                void save(
                  { minQuality: next ? "suspect" : "clean" },
                  next
                    ? "Flagged leads will now be forwarded with a review banner."
                    : "Only clean leads will be forwarded.",
                )
              }
            />
            <Toggle
              checked={destination.dailyReportEnabled}
              label="Daily summary"
              disabled={saving}
              onChange={(next) =>
                void save(
                  { dailyReportEnabled: next },
                  next ? "Daily summary on." : "Daily summary off.",
                )
              }
            />
            {/* Only meaningful while the summary itself is on. */}
            <Toggle
              checked={destination.includeCsvInDaily}
              label="Attach CSV to summary"
              disabled={saving || !destination.dailyReportEnabled}
              onChange={(next) =>
                void save({ includeCsvInDaily: next }, next ? "CSV attached." : "CSV removed.")
              }
            />
          </div>
        </section>

        {destination.notes && (
          <section className="sect">
            <p className="muted" style={{ fontSize: "var(--t-xs)" }}>
              {destination.notes}
            </p>
          </section>
        )}
      </div>
    </section>
  );
}

export function Destinations({ notify }: { notify: (tone: "ok" | "bad", text: string) => void }) {
  const state = useAsync<Destination[]>(() => loadDestinations(), []);
  // Loaded alongside, not blocking: the card is useful before the figures land.
  const counts = useAsync<Record<string, number>>(() => loadSiteLeadCounts(), []);

  if (state.error && !state.data) {
    return <ErrorState message={state.error} onRetry={state.reload} />;
  }

  const destinations = state.data ?? [];

  return (
    <div className="stack stack--5">
      <header className="page-head">
        <div>
          <h1 className="page-head__title">Destinations</h1>
          <p className="page-head__sub">
            Who receives leads, from which sites, and at what quality bar.
          </p>
        </div>
      </header>

      {state.loading ? (
        <Panel>
          <div className="stack stack--4">
            <div className="skel" style={{ height: 18, width: "30%" }} />
            <div className="skel" style={{ height: 13, width: "60%" }} />
            <div className="skel" style={{ height: 34, width: "100%" }} />
          </div>
        </Panel>
      ) : destinations.length === 0 ? (
        <Panel flush>
          <EmptyState
            icon={<BuildingsIcon size={22} />}
            title="No partners configured"
            body="Add one from the terminal with: pnpm destinations set --key acme --name 'Acme' --emails leads@acme.com"
          />
        </Panel>
      ) : (
        <div className="stack stack--5">
          {destinations.map((destination) => (
            <DestinationCard
              key={destination.id}
              destination={destination}
              notify={notify}
              counts={counts.data ?? null}
              onSaved={(next) =>
                state.patch((list) =>
                  list.map((item) => (item.id === next.id ? next : item)),
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
